import { FlexLayout } from '@deity/falcon-ui';
import { Box, Button } from '@deity-io/portal-ui';
import { TabItem, Tabs } from 'components';
import type { EntityTab } from 'contracts/interfaces';
import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Helmet } from 'react-helmet';
import {
  useExecuteStatusCommandMutation,
  useOnManagementCommandLogLineAddedSubscription
} from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';
import type { IRuntimeState } from './IRuntimeState';
import { RuntimeEventAggregator } from './RuntimeAggregator';
import BtnProducersTab from './tabs/BtnProducersTab';
import PackagesTab from './tabs/Packages';
import RestGatewayTab from './tabs/RestGatewayTab';

interface IStatusTabProps {
  isEditable?: boolean;
}

/**
 * call status. wait for returning exchange created.
 * when created, start listening for log lines
 */
const RuntimeStatus: FC<IStatusTabProps> = ({ isEditable = true }) => {
  const inputRef = useRef<any>(null);
  const [clusterState, setClusterState] = useState<IRuntimeState>({ services: {}, packages: {} });
  const idleTimeOut = useRef<null | NodeJS.Timeout>(null);
  const stopIdleTimer = () => {
    if (idleTimeOut.current) {
      const timer = idleTimeOut.current;
      idleTimeOut.current = null;
      clearTimeout(timer);
    }
  };

  const startIdleTimer = () => {
    idleTimeOut.current = setTimeout(() => {
      const newState = { ...componentState };
      delete newState.sessionUuid;
      stopIdleTimer();
      setComponentState(newState);
    }, 60 * 1000);
  };

  const [componentState, setComponentState] = useState<{ sessionUuid?: string }>({});
  const [executeCommand, { data: mutationResult, loading: runningStatusMutation }] = useExecuteStatusCommandMutation();
  const {
    data: logData,
    loading: subscriptionLoading,
    error: subscriptionError
  } = useOnManagementCommandLogLineAddedSubscription({
    variables: {
      uuid: componentState.sessionUuid || 'undefined',
      exchangeName: mutationResult?.executeStatusCommand.exchangeName || 'undefined',
      queueName: mutationResult?.executeStatusCommand.queueName || 'undefined'
    },
    skip: componentState.sessionUuid === undefined || mutationResult?.executeStatusCommand.exchangeName === undefined,
    onSubscriptionComplete() {
      stopIdleTimer();
    },
    onSubscriptionData: dataHolder => {
      stopIdleTimer();
      try {
        const data = dataHolder.subscriptionData?.data?.managementCommandLogLineAdded;
        if (data) {
          const { action, uuid, ...info } = data;
          // const event = ServerStatusEventFactory.create(data);
          if (inputRef.current) {
            inputRef.current.value += JSON.stringify(data.message, null, 2);
          }
          const newState = RuntimeEventAggregator.add(clusterState, data);
          if (newState) {
            setClusterState(newState);
          }
        }
      } finally {
        startIdleTimer();
      }
    }
  });
  /**
   * Bring up and tear down the graph component
   */
  useEffect(() => {
    if (!componentState.sessionUuid) {
      refresh();
    }

    // Specify how to clean up after this effect:
    return function cleanup() {
      stop();
    };
  }, []);

  const refresh = () => {
    if (!runningStatusMutation) {
      const myUuid = v4();
      // clear the cluster state
      setClusterState({ services: {}, packages: {} });
      // set new component sessionUuid
      setComponentState({ ...componentState, sessionUuid: myUuid });
      // run query
      executeCommand({ variables: { uuid: myUuid } }).then(() => {
        startIdleTimer();
      });
    }
  };

  const stop = () => {
    const newState = { ...componentState };
    delete newState.sessionUuid;
    stopIdleTimer();
    setComponentState(newState);
  };

  if (subscriptionError) {
    // eslint-disable-next-line no-console
    console.log('subscriptionError', subscriptionError);
  }

  const tabs: EntityTab[] = [
    {
      label: 'Packages',
      isActive: true,
      content: <PackagesTab clusterState={clusterState} isEditable={isEditable} loading={logData !== undefined} />
    },
    {
      label: 'Rest gateway',
      content: <RestGatewayTab clusterState={clusterState} isEditable={isEditable} loading={logData !== undefined} />
    },
    {
      label: 'Manual triggers',
      content: <BtnProducersTab clusterState={clusterState} isEditable={isEditable} loading={logData !== undefined} />
    },
    { label: 'Scheduler', content: <>@todo</> }
  ];

  return (
    <Box>
      <Helmet>
        <title>Runtime status</title>
      </Helmet>
      <FlexLayout p="xs" justifyContent="flex-end">
        <Button
          key="query"
          disabled={!(isEditable && !runningStatusMutation && logData === undefined)}
          onClick={refresh}
        >
          Refresh
        </Button>
        <Button key="stop" disabled={!isEditable || logData === undefined} onClick={stop}>
          Stop Refreshing
        </Button>
      </FlexLayout>
      <Tabs>
        {tabs?.map(tab => (
          <TabItem label={tab.label} key={tab.label} isActive={tab.isActive ?? false} keepAlive={tab.keepAlive}>
            {tab.content}
          </TabItem>
        ))}
      </Tabs>

      {/* <Input type="text" multiline fullWidth inputRef={inputRef} /> */}
    </Box>
  );
};

export default RuntimeStatus;
